/**
 * The entity-kind ⇄ queryable-type correspondence (REQ-UI-183 / REQ-UI-184).
 *
 * One table, two readers, and it exists because the same stored record is
 * reachable by two routes that used to render it two ways:
 *
 * - **Query → entity.** A Query result row of a type that *is* an entity
 *   opens that entity's own sections, not the generic attribute list, so
 *   an operator who found a detection by query sees the classification
 *   tree, signal, behaviour and provenance a Detections-panel click has
 *   always shown (:func:`entityKindForRecordType`). ADR-041 D-6 did this
 *   for ``sapient_track`` alone; 2026-10-05 generalized it.
 * - **Entity → query.** An entity pane addresses its own stored record to
 *   serve the SOURCE BYTES drawer (:func:`recordTypeForEntityKind`).
 *   Each type's catalog ``id_attribute`` is exactly the id the selection
 *   bus already carries — ``report_id`` for a detection, ``alert_id`` for
 *   an alert, ``producer_id`` for a platform — so no new addressing was
 *   needed on either side.
 *
 * # The two asymmetries, both deliberate
 *
 * **A platform and an aggregator share one type.** Both producer scopes
 * are rows of the one ``control_plane.platform_registration`` table
 * (``scope`` tells them apart), so the entity → type direction maps both
 * and the type → entity direction picks ``platform``. It picks that one
 * because the Query panel is not where an aggregator is operated: an
 * aggregator is a Provider (ADR-040), its roster and controls are Flow V
 * reached from the System panel, and ``AggregatorSections`` describes
 * connectivity and multiplexing rather than anything a registration row
 * holds. A registration row therefore reads as what it is — a producer's
 * registration — on the platform pane.
 *
 * **A track maps to no type for bytes.** ``sapient_track`` is a *queryable*
 * type (the query → entity direction maps it) but carries no wire bytes:
 * a track is a derivation over detections, with no payload column and
 * nothing the BFF could reconstruct. The entity → type direction returns
 * ``null`` and the drawer states :data:`NO_WIRE_BYTES_NOTE`, rather than
 * the section disappearing — on a pane where every sibling kind has
 * bytes, an absent section reads as a fault (CLAUDE.md §8.1).
 *
 * Cross-references:
 *   - REQ-UI-183 (one pane per record, whichever surface reached it),
 *     REQ-UI-184 (SOURCE BYTES on every entity kind), REQ-UI-176 (the
 *     ``sapient_track`` precedent this generalizes).
 *   - REQ-QUERY-040 (the detail route's ``id_attribute`` addressing).
 *   - ADR-038 §Decision 5; ADR-041 D-5 / D-6; ADR-040 (an aggregator is a
 *     Provider).
 */
import type { EntitySelection } from "@/selection/selection-store";

/** The entity kinds the selection bus carries as domain entities. */
export type EntityKind = EntitySelection["kind"];

/**
 * Queryable type id → the entity kind whose sections render it.
 *
 * Absent means "no entity kind" and the generic record view stands. That
 * is the right answer for the status samples (no entity), raw blobs (an
 * object, not an entity) and the system-status types (synthesized), and
 * it is also the safe answer for a type this build has never heard of.
 */
const ENTITY_KIND_BY_RECORD_TYPE: Readonly<Record<string, EntityKind>> = {
  sapient_detection: "detection",
  sapient_alert: "alert",
  sapient_track: "track",
  // Both scopes live in one table; see the module docstring for why this
  // direction resolves to the platform pane.
  sapient_registration: "platform",
};

/**
 * Entity kind → the queryable type whose record carries its originating
 * bytes, or ``null`` for a kind that has none.
 */
const RECORD_TYPE_BY_ENTITY_KIND: Readonly<Record<EntityKind, string | null>> = {
  platform: "sapient_registration",
  aggregator: "sapient_registration",
  detection: "sapient_detection",
  alert: "sapient_alert",
  track: null,
};

/**
 * Why an entity kind has no originating wire bytes, keyed by kind.
 *
 * Rendered in the SOURCE BYTES section in place of the drawer. States the
 * fact *and* where the bytes actually are, because "no bytes" on its own
 * leaves the operator with nowhere to go.
 *
 * **Architect-specified copy** (2026-10-10). The previous wording said the
 * same three things across three sentences; on a panel whose job is density
 * the shorter form says them once. It is asserted verbatim by this module's
 * test rather than by keyword, so a reword is a deliberate edit to both
 * rather than something a loose regex waves through.
 */
export const NO_WIRE_BYTES_NOTE: Readonly<Partial<Record<EntityKind, string>>> = {
  track:
    "Tracks are derived from detection reports. See the track's detection reports for " +
    "their raw message bytes.",
};

/**
 * The entity kind a query record of ``typeId`` is, or ``null`` when the
 * type has no entity counterpart.
 *
 * @param typeId - A queryable type id from the catalog.
 * @returns The entity kind whose sections should render the record.
 */
export function entityKindForRecordType(typeId: string): EntityKind | null {
  return ENTITY_KIND_BY_RECORD_TYPE[typeId] ?? null;
}

/**
 * The queryable type whose record holds ``kind``'s originating bytes.
 *
 * @param kind - An entity kind from the selection bus.
 * @returns The type id, or ``null`` when the kind has no wire bytes.
 */
export function recordTypeForEntityKind(kind: EntityKind): string | null {
  return RECORD_TYPE_BY_ENTITY_KIND[kind];
}
